import { EMedEventTypes, EValidationType, TMedEventUI, TPatientMedEvent } from 'src/types/med-event'
import { TConfigUI, TValidationRule } from 'src/types/followup'
import { TCompletedMedEvent } from './generic-followup-form.type'

const sortCompletedMedEvent = (completedMedEvents: TCompletedMedEvent[]) => {
  return completedMedEvents.sort((previousPatientMedEvent, nextPatientMedEvent) => {
    return (
      new Date(nextPatientMedEvent.date || '').getTime() -
      new Date(previousPatientMedEvent.date || '').getTime()
    )
  })
}

const getInitialCompletedMedEvent = (
  followupMedEventUi: TMedEventUI[],
  patientMedEvents: TPatientMedEvent[],
): TCompletedMedEvent[] => {
  if (!patientMedEvents?.length) {
    return []
  }
  const completedMedEvents = patientMedEvents.map((patientMedEvent) => {
    const medEventUI = followupMedEventUi.find((item) => item.MedEvent.id === patientMedEvent.MedEventId)
    const uniqId = `completed-patient-medevents-${patientMedEvent.id}-${patientMedEvent.MedEventId}`

    return {
      LocationId: patientMedEvent.Location?.id,
      UserId: patientMedEvent.MedicalStaffMember?.id,
      date: patientMedEvent.date,
      convocationDate: patientMedEvent.convocationDate,
      medEventUI,
      patientMedEventId: patientMedEvent.id,
      interopMetadata: patientMedEvent.interopMetadata,
      uniqId,
    }
  })

  return sortCompletedMedEvent(completedMedEvents)
}

const hasCompletedMedEventsChanged = (
  current: TCompletedMedEvent[],
  initial: TCompletedMedEvent[],
): boolean => {
  if (current.length !== initial.length) {
    return true
  }

  let hasChanged = false
  for (const initialEvent of initial) {
    const correspondingCurrentEvent = current.find((event) => event.uniqId === initialEvent?.uniqId)
    if (!correspondingCurrentEvent || !initialEvent) {
      break
    }

    if (
      correspondingCurrentEvent.LocationId !== initialEvent.LocationId ||
      correspondingCurrentEvent.UserId !== initialEvent.UserId ||
      correspondingCurrentEvent.date?.toString() !== initialEvent.date?.toString() ||
      correspondingCurrentEvent.convocationDate?.toString() !== initialEvent.convocationDate?.toString() ||
      correspondingCurrentEvent.medEventUI?.MedEvent.id !== initialEvent.medEventUI?.MedEvent.id
    ) {
      hasChanged = true
    }
  }

  return hasChanged
}

const checkRepeatableMedEventRule = (
  completedPatientMedEvents: TCompletedMedEvent[],
  newMedEventUI: TMedEventUI,
): boolean => {
  const samePatientMedEventsCount =
    completedPatientMedEvents?.filter((pme) => pme.medEventUI?.MedEvent.id === newMedEventUI.MedEvent.id)
      ?.length || 0

  const isRepeatableMedEvent = newMedEventUI.isRepeatable
  if (!isRepeatableMedEvent && samePatientMedEventsCount > 0) {
    return false
  }
  if (!isRepeatableMedEvent) {
    return true
  }

  const maxRepetitions = newMedEventUI.maxRepetitions
  if (!maxRepetitions) {
    return true
  }
  return samePatientMedEventsCount < maxRepetitions
}

const getErrorMessageIfMinIsNotReached = (
  t: any,
  type: EMedEventTypes,
  validationErrors?: TValidationRule[],
) => {
  const validationErrorOfType = validationErrors?.find((validationError) => validationError.type === type)
  const errorMessage = validationErrorOfType?.min
    ? t('components.generic-followups-form.fill-at-least', {
        minNumber: validationErrorOfType.min,
        consultationType: validationErrorOfType.type,
      })
    : undefined
  return errorMessage
}

const checkValidationRules = (
  completedPatientMedEvents: TCompletedMedEvent[],
  newMedEventUI: TMedEventUI,
  configUI: TConfigUI,
) => {
  const validationRules = configUI?.validationRules?.filter((vr) => vr.type === newMedEventUI.MedEvent.type)
  if (!validationRules?.length) {
    return true
  }
  const validationRuleOfType = validationRules?.find(
    (validationRule) => validationRule.type === newMedEventUI.MedEvent.type,
  )
  if (!validationRules?.length || !validationRuleOfType?.max) return false
  const medEventIdsSeen = new Set()
  medEventIdsSeen.add(newMedEventUI.MedEvent.id)
  let patientMedEventWithSameTypeCount = 1 // for the newMedEvent added
  for (const completedPatientMedEvent of completedPatientMedEvents) {
    const currentMedEventId = completedPatientMedEvent.medEventUI?.MedEvent.id
    // we only want to count uniq med event
    if (medEventIdsSeen.has(currentMedEventId)) {
      continue
    }
    if (completedPatientMedEvent.medEventUI?.MedEvent.type === newMedEventUI.MedEvent.type) {
      medEventIdsSeen.add(currentMedEventId)
      patientMedEventWithSameTypeCount++
    }
  }
  return patientMedEventWithSameTypeCount <= validationRuleOfType.max
}

const canAddMedEventToPatientFollowup = (
  completedPatientMedEvents: TCompletedMedEvent[],
  newMedEventUI: TMedEventUI,
  configUI: TConfigUI,
): boolean => {
  const isRepeatableRuleRespected = checkRepeatableMedEventRule(completedPatientMedEvents, newMedEventUI)
  const isValidationRuleByTypeRespected = checkValidationRules(
    completedPatientMedEvents,
    newMedEventUI,
    configUI,
  )
  return isRepeatableRuleRespected && isValidationRuleByTypeRespected
}

const getMedEventsToCompleteByValidationType = (
  configUI: TConfigUI,
  validationType: EValidationType,
  completedPatientMedEvents: TCompletedMedEvent[],
): TMedEventUI[] => {
  return (
    configUI?.medEventsUI
      ?.filter((medEventUI) => medEventUI.validationType === validationType)
      ?.filter((medEventUI) =>
        canAddMedEventToPatientFollowup(completedPatientMedEvents, medEventUI, configUI),
      ) || []
  )
}

const getAddableMedEventsByType = ({
  configUI,
  completedMedEvents,
}: {
  configUI: TConfigUI
  completedMedEvents: TCompletedMedEvent[]
}) => {
  const requiredMedEvents = getMedEventsToCompleteByValidationType(
    configUI,
    EValidationType.REQUIRED,
    completedMedEvents,
  )
  const recommendedMedEvents = getMedEventsToCompleteByValidationType(
    configUI,
    EValidationType.RECOMMENDED,
    completedMedEvents,
  )
  const optionalMedEvents = getMedEventsToCompleteByValidationType(
    configUI,
    EValidationType.OPTIONAL,
    completedMedEvents,
  )
  return { requiredMedEvents, recommendedMedEvents, optionalMedEvents }
}

export {
  sortCompletedMedEvent,
  getInitialCompletedMedEvent,
  hasCompletedMedEventsChanged,
  getErrorMessageIfMinIsNotReached,
  canAddMedEventToPatientFollowup,
  getAddableMedEventsByType,
}
